// 🔧 FooterWithContactForm.js
// ✅ Purpose: Reusable contact form for the global footer
// ✅ Features:
// - Saves contact messages to global `/contactForm` Firestore collection
// - Shows success/error notifications for 20 seconds
// - Keeps the form compact for footer use
// - Uses verbose logging for troubleshooting

import React, { useState } from "react";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { logger } from "../utils/logger";
import { trackEvent } from "../utils/analytics";
import { GA_EVENTS } from "../services/analytics/ga_events";

const FooterWithContactForm = () => {
  const [showForm, setShowForm] = useState(false);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const showFooterStatus = (nextStatus) => {
    setStatus(nextStatus);
    setTimeout(() => setStatus(null), 20000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = e.target;
    const name = form.name.value.trim();
    const email = form.email.value.trim();
    const message = form.message.value.trim();

    if (!name || !email || !message) {
      showFooterStatus({
        type: "danger",
        message: "❌ Please complete all contact fields.",
      });
      return;
    }

    try {
      setSubmitting(true);

      await addDoc(collection(db, "contactForm"), {
        name,
        email,
        message,
        source: "footer",
        createdAt: serverTimestamp(),
      });

      logger.log("✅ Footer contact form submitted", {
        value: email,
      });

      trackEvent({
        action: GA_EVENTS.CONTACT_FORM_SUBMITTED,
        parameters: {
          source: "footer",
          source_page: window.location.pathname,
        },
      });

      showFooterStatus({
        type: "success",
        message: "✅ Message sent successfully!",
      });

      form.reset();
    } catch (err) {
      logger.error("❌ Footer contact form error:", {
        value: err,
      });

      showFooterStatus({
        type: "danger",
        message: "❌ Failed to send message. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="footer-contact-form mt-3">
      <button
        type="button"
        className="btn btn-outline-light btn-sm"
        onClick={() => {
          const nextShowForm = !showForm;

          if (nextShowForm) {
            trackEvent({
              action: GA_EVENTS.CONTACT_FORM_OPENED,
              parameters: {
                source: "footer",
                source_page: window.location.pathname,
              },
            });
          }

          setShowForm(nextShowForm);
        }}
        >
        📨 {showForm ? "Hide Contact Form" : "Contact Us"}
      </button>

      {showForm && (
        <div className="bg-white text-dark p-3 rounded shadow-sm mt-3">
          <h6 className="fw-bold mb-3">Send us a message</h6>

          {status && (
            <div className={`alert alert-${status.type}`} role="alert">
              {status.message}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-2">
              <label htmlFor="footer-contact-name" className="form-label">
                Your Name
              </label>
              <input
                id="footer-contact-name"
                type="text"
                name="name"
                className="form-control"
                required
              />
            </div>

            <div className="mb-2">
              <label htmlFor="footer-contact-email" className="form-label">
                Your Email
              </label>
              <input
                id="footer-contact-email"
                type="email"
                name="email"
                className="form-control"
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="footer-contact-message" className="form-label">
                Message
              </label>
              <textarea
                id="footer-contact-message"
                name="message"
                rows={3}
                className="form-control"
                required
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default FooterWithContactForm;